import { useState, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { getSupabaseClient } from '@/lib/supabase';
import { downloadImage } from '@/lib/downloadImage';

interface ViewerState {
  imageUrl: string;
  title?: string;
  imageType?: string;
}

const typeLabels: Record<string, string> = {
  finishing: '후가공 미리보기',
  printing: '인쇄 미리보기',
  assembly: '부품조립 미리보기',
};

const MIN_SCALE = 1;
const MAX_SCALE = 6;

export default function ImageViewerPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const state = location.state as ViewerState | null;

  const imageUrl = state?.imageUrl || '';
  const title = state?.title || '미리보기 이미지';
  const imageType = state?.imageType || 'finishing';
  const typeLabel = typeLabels[imageType] || '미리보기 이미지';

  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);
  const [saveState, setSaveState] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const [saveMessage, setSaveMessage] = useState<string | null>(null);

  // Zoom / pan state
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });

  const containerRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<{
    dragging: boolean;
    startClientX: number;
    startClientY: number;
    startX: number;
    startY: number;
  }>({
    dragging: false,
    startClientX: 0,
    startClientY: 0,
    startX: 0,
    startY: 0,
  });

  const clampScale = useCallback((value: number) => {
    return Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));
  }, []);

  const resetView = useCallback(() => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  }, []);

  const zoomBy = useCallback(
    (factor: number) => {
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const cx = rect.width / 2;
      const cy = rect.height / 2;

      setScale((prevScale) => {
        const nextScale = clampScale(prevScale * factor);
        const ratio = nextScale / prevScale;
        setTranslate((prev) => {
          if (nextScale <= 1) return { x: 0, y: 0 };
          const dx = cx - (cx - prev.x) * ratio;
          const dy = cy - (cy - prev.y) * ratio;
          return { x: dx, y: dy };
        });
        return nextScale;
      });
    },
    [clampScale],
  );

  const handleWheel = useCallback(
    (e: React.WheelEvent<HTMLDivElement>) => {
      e.preventDefault();
      const container = containerRef.current;
      if (!container) return;
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      const mouseY = e.clientY - rect.top;
      const cx = rect.width / 2;
      const cy = rect.height / 2;

      const factor = e.deltaY < 0 ? 1.15 : 1 / 1.15;

      setScale((prevScale) => {
        const nextScale = clampScale(prevScale * factor);
        const ratio = nextScale / prevScale;
        setTranslate((prev) => {
          if (nextScale <= 1) return { x: 0, y: 0 };
          const dx = mouseX - cx;
          const dy = mouseY - cy;
          const newX = dx - (dx - prev.x) * ratio;
          const newY = dy - (dy - prev.y) * ratio;
          return { x: newX, y: newY };
        });
        return nextScale;
      });
    },
    [clampScale],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current = {
      dragging: true,
      startClientX: e.clientX,
      startClientY: e.clientY,
      startX: 0,
      startY: 0,
    };
    setTranslate((prev) => {
      dragRef.current.startX = prev.x;
      dragRef.current.startY = prev.y;
      return prev;
    });
    if (containerRef.current) {
      containerRef.current.setPointerCapture(e.pointerId);
    }
  }, []);

  const handlePointerMove = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current.dragging) return;
    const dx = e.clientX - dragRef.current.startClientX;
    const dy = e.clientY - dragRef.current.startClientY;
    setTranslate({ x: dragRef.current.startX + dx, y: dragRef.current.startY + dy });
  }, []);

  const handlePointerUp = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    dragRef.current.dragging = false;
    if (containerRef.current) {
      containerRef.current.releasePointerCapture(e.pointerId);
    }
  }, []);

  const handleDoubleClick = useCallback(() => {
    setScale((prevScale) => {
      if (prevScale > 1) {
        resetView();
        return prevScale;
      }
      zoomBy(2);
      return prevScale;
    });
  }, [resetView, zoomBy]);

  const handleClose = useCallback(() => {
    navigate(-1);
  }, [navigate]);

  const handleDownload = useCallback(() => {
    const safeName = `${typeLabel} ${new Date().toISOString().slice(0, 10)}.png`;
    downloadImage(imageUrl, safeName);
  }, [imageUrl, typeLabel]);

  const handleSave = useCallback(async () => {
    if (!imageUrl) return;
    setSaveState('saving');
    setSaveMessage(null);

    const client = getSupabaseClient();
    if (!client) {
      setSaveState('error');
      setSaveMessage('연결에 실패했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    const { data: { user } } = await client.auth.getUser();
    if (!user) {
      setSaveState('error');
      setSaveMessage('로그인이 필요합니다. 로그인 후 다시 시도해주세요.');
      return;
    }

    const { error } = await client.from('saved_images').insert({
      user_id: user.id,
      image_url: imageUrl,
      title,
      image_type: imageType,
    });

    if (error) {
      setSaveState('error');
      setSaveMessage('저장 중 오류가 발생했습니다. 잠시 후 다시 시도해주세요.');
      return;
    }

    setSaveState('saved');
    setSaveMessage('내 보관함에 저장되었습니다.');
  }, [imageUrl, title, imageType]);

  const isZoomed = scale > 1;

  return (
    <div className="min-h-screen bg-black flex flex-col">
      {/* Top bar */}
      <div className="relative z-50 flex items-center justify-between px-4 md:px-6 py-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="w-7 h-7 rounded-lg bg-white/10 flex items-center justify-center shrink-0">
            <i className="ri-image-line text-white/70 text-sm" />
          </span>
          <span className="text-sm font-bold text-white/90 truncate">{typeLabel}</span>
        </div>
        <button
          onClick={handleClose}
          className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors"
          title="닫기"
        >
          <i className="ri-close-line text-lg" />
        </button>
      </div>

      {/* Image area */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerCancel={handlePointerUp}
        onDoubleClick={handleDoubleClick}
        className="flex-1 flex items-center justify-center p-4 md:p-8 pb-28 overflow-hidden touch-none select-none"
        style={{ cursor: isZoomed ? 'grab' : 'default' }}
      >
        {!imageUrl ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <i className="ri-image-line text-5xl text-white/20" />
            <p className="text-sm text-white/50">이미지를 찾을 수 없습니다.</p>
            <button
              onClick={() => navigate('/packaging')}
              className="px-5 py-2.5 text-sm font-bold text-white bg-white/10 rounded-lg hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap"
            >
              상품 목록으로
            </button>
          </div>
        ) : imageError ? (
          <div className="flex flex-col items-center gap-4 text-center">
            <i className="ri-error-warning-line text-5xl text-white/20" />
            <p className="text-sm text-white/50">이미지를 불러올 수 없습니다.</p>
          </div>
        ) : (
          <>
            {!imageLoaded && (
              <div className="flex flex-col items-center gap-3">
                <div className="w-10 h-10 border-[3px] border-white/20 border-t-white rounded-full animate-spin" />
                <p className="text-white/50 text-sm">이미지 불러오는 중...</p>
              </div>
            )}
            <img
              src={imageUrl}
              alt={title}
              onLoad={() => setImageLoaded(true)}
              onError={() => setImageError(true)}
              className={`max-w-full max-h-[calc(100vh-140px)] object-contain select-none ${imageLoaded && !imageError ? 'block' : 'hidden'}`}
              draggable={false}
              style={{
                transform: `translate(${translate.x}px, ${translate.y}px) scale(${scale})`,
                transformOrigin: 'center',
                transition: 'transform 0.08s linear',
              }}
            />
          </>
        )}
      </div>

      {/* Zoom controls - floating on the right */}
      <div className="fixed right-4 md:right-6 top-1/2 -translate-y-1/2 z-50 flex flex-col gap-2">
        <button
          onClick={() => zoomBy(1.3)}
          className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors"
          title="확대"
        >
          <i className="ri-add-line text-lg" />
        </button>
        <button
          onClick={() => zoomBy(1 / 1.3)}
          className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors"
          title="축소"
        >
          <i className="ri-subtract-line text-lg" />
        </button>
        <button
          onClick={resetView}
          className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 text-white flex items-center justify-center cursor-pointer transition-colors"
          title="원래 크기"
        >
          <i className="ri-fullscreen-exit-line text-lg" />
        </button>
        <span className="mx-auto mt-1 px-2 py-1 rounded-full bg-black/50 text-white/80 text-xs font-bold whitespace-nowrap">
          {Math.round(scale * 100)}%
        </span>
      </div>

      {/* Bottom action bar - floating, always visible regardless of zoom/pan */}
      <div className="fixed bottom-0 left-0 right-0 z-50 border-t border-white/10 bg-black/60 backdrop-blur-md px-4 md:px-6 py-4">
        <div className="w-full flex items-center justify-center gap-3">
          {saveMessage && (
            <span
              className={`hidden md:inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold ${
                saveState === 'saved'
                  ? 'bg-emerald-500/20 text-emerald-300'
                  : 'bg-rose-500/20 text-rose-300'
              }`}
            >
              <i className={saveState === 'saved' ? 'ri-check-line' : 'ri-error-warning-line'} />
              {saveMessage}
            </span>
          )}

          <button
            onClick={handleClose}
            className="flex-1 max-w-[200px] py-3 rounded-xl text-sm font-bold text-white bg-white/10 hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
          >
            <i className="ri-close-line" />
            닫기
          </button>

          <button
            onClick={handleSave}
            disabled={saveState === 'saving' || !imageUrl}
            className="flex-1 max-w-[200px] py-3 rounded-xl text-sm font-bold text-black bg-white hover:bg-cream-50 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saveState === 'saving' ? (
              <>
                <div className="w-4 h-4 border-2 border-black/30 border-t-black rounded-full animate-spin" />
                저장 중...
              </>
            ) : (
              <>
                <i className="ri-bookmark-line" />
                저장
              </>
            )}
          </button>

          <button
            onClick={handleDownload}
            disabled={!imageUrl}
            className="flex-1 max-w-[200px] py-3 rounded-xl text-sm font-bold text-white bg-white/10 hover:bg-white/20 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <i className="ri-download-line" />
            다운로드
          </button>
        </div>

        {/* Mobile save feedback */}
        {saveMessage && (
          <p
            className={`md:hidden mt-2 text-center text-xs font-bold ${
              saveState === 'saved' ? 'text-emerald-300' : 'text-rose-300'
            }`}
          >
            {saveMessage}
          </p>
        )}
      </div>
    </div>
  );
}